<template>
  <div class="video-booth">
    <header class="panel-header">
      <div class="panel-left-item">
        <button class="text-button narrow-button" @click="close" v-t>Close</button>
      </div>
      <div class="panel-title" v-t>Video booth</div>
      <div class="panel-right-item">
        <button v-show="hasChunks && !recording" class="text-button narrow-button" @click="done" v-t>
          Playback and save
        </button>
      </div>
    </header>
    <div class="panel-body" :style="{ height: canvasHeight + 'px' }">
      <div v-show="!streaming" class="panel-interstitial tv">
        <div class="tv-cell">
          <loading-spinner />
        </div>
        <div class="tv-cell">
          <p v-t>Starting camera and microphone ...</p>
          <p class="caption-type" v-t>Make sure you have given us permission to your camera and microphone.</p>
        </div>
      </div>
      <video-instructions v-if="streaming && !recording && !hasChunks" />
      <!-- without muted attribute an echo is generated -->
      <video v-show="streaming" ref="player" :width="canvasWidth" :height="canvasHeight" autoplay muted />
      <div class="video-booth-meta inverse-scheme">
        <div v-show="recording || secondsElapsed > 0" class="video-booth-duration">{{ recordingDurationText }}</div>
        <div v-show="recording" class="video-booth-beacon">
          <span class="video-booth-beacon-text">Rec</span><base-icon name="record" />
        </div>
        <div v-show="!recording && hasChunks" class="video-booth-meta-overlay">
          <div v-show="!hasReachedRecordingLimit && hasChunks" class="caption-type" v-t>Paused</div>
          <div v-show="hasReachedRecordingLimit" v-t>Sorry, you've run out of time.</div>
        </div>
      </div>
    </div>
    <footer v-show="!recording" class="panel-footer">
      <div class="video-booth-device-selector">
        <div v-if="audioDevices.length > 1" class="select-box video-booth-audio-select-box">
          <base-icon name="mic" />
          <select @change="chooseAudioDevice">
            <option
              v-for="audioDevice in audioDevices"
              :key="audioDevice.id"
              :selected="audioDevice.id == activeAudioDeviceId"
              :value="audioDevice.id"
              >{{ audioDevice.label }}</option
            >
          </select>
        </div>
        <div v-if="videoDevices.length > 1" class="select-box video-booth-video-select-box">
          <base-icon name="camera" />
          <select @change="chooseVideoDevice">
            <option
              v-for="videoDevice in videoDevices"
              :key="videoDevice.id"
              class="ellipsified-type"
              :selected="videoDevice.id == activeVideoDeviceId"
              :value="videoDevice.id"
              >{{ videoDevice.label }}</option
            >
          </select>
        </div>
      </div>
      <button
        v-show="streaming && !hasReachedRecordingLimit"
        class="floating-action-button"
        :title="recordingButtonTitle"
        @click="startRecording"
      >
        <base-icon name="videocam" />
      </button>
      <button v-show="hasChunks" class="text-button narrow-button" @click="restartRecording" v-t>
        Discard and restart
      </button>
    </footer>
    <footer v-show="recording" class="panel-footer">
      <button class="floating-action-button video-booth-pause" @click="pauseRecording" v-t:title="'Pause recording'">
        <base-icon name="pause" />
      </button>
      <div v-if="isReachingRecordingLimit && !hasReachedRecordingLimit" class="video-booth-runningout warning-type" v-t>
        You're running out of time.
      </div>
    </footer>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import IntlMixin from 'vuemixins/intl_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import AVRecorderMixin from 'vuemixins/av_recorder_mixin'
import LoadingSpinner from 'vuecomponents/loading_spinner'
import VideoInstructions from 'vuecomponents/video_instructions'
import { trackEvent } from 'bits/analytics'

export default {
  components: {
    BaseIcon,
    LoadingSpinner,
    VideoInstructions,
  },
  mixins: [IntlMixin, AVRecorderMixin],
  props: {
    width: Number,
    height: Number,
  },
  data() {
    return {
      maxDuration: 300, // in seconds
      bufferDuration: 15,
      canvasWidth: 640,
      canvasHeight: 360,
    }
  },
  computed: {
    constraints() {
      return {
        audio: {
          deviceId: this.activeAudioDeviceId ? { exact: this.activeAudioDeviceId } : undefined,
        },
        video: {
          deviceId: this.activeVideoDeviceId ? { exact: this.activeVideoDeviceId } : undefined,
          width: 3840,
          height: 2160,
        },
      }
    },
    isReachingRecordingLimit() {
      return this.secondsElapsed >= this.maxDuration - 30
    },
  },
  mounted() {
    // set values given by the parent
    this.canvasWidth = this.width
    this.canvasHeight = this.height
    this.mountRecording('video')
    this.mountStreaming()
  },
  beforeDestroy() {
    this.stopRecording()
    this.stopStreaming()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    done() {
      /**
       * When you are done, there are two possibilities:
       * 1. We already have recording data, in which case, just emit the event.
       * 2. We don't have data and we need to wait for recording data to be available
       *    before we fire the done event.
       *    (When recording data is available, gotRecordingData is automatically called.)
       */
      trackEvent('Video Booth', 'Recorded', null, this.secondsElapsed)
      if (this.recordingStatus === 'stopped') {
        this.$emit('done', this.recordedBlob())
      } else {
        this.stopRecording()
      }
    },
    gotRecordedData() {
      this.$emit('done', this.recordedBlob())
    },
    gotStream(stream) {
      const videoTrack = stream.getVideoTracks()[0]
      // not supported properly by many browsers, e.g. Safari
      if (typeof videoTrack.getSettings === 'function') {
        const settings = videoTrack.getSettings()
        if (settings.aspectRatio) {
          this.canvasHeight = Math.floor(this.canvasWidth / settings.aspectRatio)
        }
      }
      this.$emit('stream', { canvasHeight: this.canvasHeight })
      this.$refs.player.srcObject = stream
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.video-booth {
  footer {
    position: relative;
  }
}
.floating-action-button {
  position: absolute;
  top: -28px;
  left: calc(50% - 28px);
}
video {
  display: block;
}
.panel-body {
  position: relative;
}
.video-booth-meta {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.video-booth-meta-overlay {
  width: 100%;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.54);
}
.video-booth-duration {
  position: absolute;
  left: 16px;
  bottom: 8px;
}
.video-booth-beacon {
  position: absolute;
  bottom: 8px;
  right: 16px;
  display: flex;
  align-items: center;
  i {
    //the symbol is not aligned.
    margin-top: -2px;
    margin-left: 4px;
    color: $red-color;
  }
}
.video-booth-beacon-text {
  line-height: 24px;
  font-size: 13px;
  text-transform: uppercase;
}
.video-booth-runningout {
  font-size: 14px;
  padding: 0px 8px;
}
.video-booth-device-selector {
  display: flex;
  .select-box {
    margin-right: 8px;
  }
}
select {
  max-width: 72px;
}
.select-box {
  padding-left: 24px;
}
.select-box i {
  position: absolute;
  top: 7px;
  left: 4px;
  line-height: 18px;
  color: var(--muted-text-color);
}
</style>
